/**
 * Tenant CustomPermissionDefinition overlay for catalog + licensing.
 * Never grants actions by itself — Role.roleAuthorization remains the grant path.
 */

import { PAGE_REGISTRY, MODULES, BASE_ACTIONS } from './registry.js';

const _byKey = new Map(PAGE_REGISTRY.map((p) => [p.key, p]));

function getStaticPageDef(pageKey) {
    if (pageKey == null || pageKey === '') return null;
    const key = String(pageKey).trim();
    return _byKey.get(key) ?? PAGE_REGISTRY.find((p) => p.key === key) ?? null;
}

function getCustomDef(pageKey, customDefsByPageKey) {
    if (!customDefsByPageKey) return null;
    const key = String(pageKey).trim();
    if (customDefsByPageKey instanceof Map) return customDefsByPageKey.get(key) || null;
    if (typeof customDefsByPageKey === 'object') return customDefsByPageKey[key] || null;
    return null;
}

/**
 * @param {string} pageKey
 * @param {Map<string, object>|Record<string, object>|null|undefined} customDefsByPageKey
 * @returns {object|null}
 */
export function resolveEffectivePageDef(pageKey, customDefsByPageKey) {
    if (pageKey == null || pageKey === '') return null;
    const key = String(pageKey).trim();
    const staticDef = getStaticPageDef(key);
    const custom = getCustomDef(key, customDefsByPageKey);
    if (!staticDef && !custom) return null;
    if (!custom) return staticDef;
    if (!staticDef) {
        return {
            key,
            module: custom.moduleKey || MODULES.CORE || 'core',
            actions: Array.isArray(custom.actions) && custom.actions.length
                ? [...custom.actions]
                : [...BASE_ACTIONS],
            group: 'other',
            path: '',
            customOverlay: true,
        };
    }
    return {
        ...staticDef,
        actions: [...new Set([...(staticDef.actions || []), ...(custom.actions || [])])],
        customOverlay: true,
    };
}

/**
 * License check with optional custom definition overlay.
 * @param {string} pageKey
 * @param {string[]} [licensedModules]
 * @param {{ customDefsByPageKey?: Map|object }} [opts]
 */
export function isPageLicensedWithOverlay(pageKey, licensedModules = [], opts = {}) {
    const def = resolveEffectivePageDef(pageKey, opts.customDefsByPageKey);
    if (!def) return false;
    const modules = Array.isArray(licensedModules) ? licensedModules : [];
    const candidates = Array.isArray(def.anyModule) && def.anyModule.length
        ? def.anyModule
        : [def.module];
    return candidates.some((mod) => {
        if (mod === MODULES.CORE || mod === 'core' || !mod) return true;
        return modules.includes(mod);
    });
}

/**
 * @param {string} pageKey
 * @param {{ customDefsByPageKey?: Map|object }} [opts]
 */
export function getActionsForPageWithOverlay(pageKey, opts = {}) {
    const def = resolveEffectivePageDef(pageKey, opts.customDefsByPageKey);
    return def?.actions ? [...def.actions] : [...BASE_ACTIONS];
}

/**
 * Licensed page keys from PAGE_REGISTRY ∪ active custom definitions.
 * @param {string[]} [licensedModules]
 * @param {{ customDefsByPageKey?: Map|object }} [opts]
 * @returns {string[]}
 */
export function getAvailablePagesWithOverlay(licensedModules = [], opts = {}) {
    const keys = new Set(
        PAGE_REGISTRY
            .filter((p) => isPageLicensedWithOverlay(p.key, licensedModules, opts))
            .map((p) => p.key),
    );
    const custom = opts.customDefsByPageKey;
    if (!custom) return [...keys];
    const customKeys = custom instanceof Map ? [...custom.keys()] : Object.keys(custom);
    for (const key of customKeys) {
        if (isPageLicensedWithOverlay(key, licensedModules, opts)) {
            keys.add(key);
        }
    }
    return [...keys];
}
